import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, PlayCircle } from "lucide-react";
import { Link } from "react-router-dom";
import api from "../../api/api";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const VideoGalleryPage = () => {
  const pageRef = useRef(null);
  const [videos, setVideos] = useState([]);

  const loadVideos = async () => {
    const res = await api.get("/gallery/videos?status=published");
    setVideos(res.data || []);
  };

  useEffect(() => {
    loadVideos();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", { opacity: 0, y: 30, duration: 0.8 });
      gsap.from(".hero-title", { opacity: 0, y: 80, duration: 1.1 });
      gsap.from(".hero-text", { opacity: 0, y: 40, duration: 0.9, delay: 0.25 });
      gsap.from(".video-card", {
        opacity: 0,
        y: 50,
        duration: 0.9,
        stagger: 0.08,
      });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, [videos]);

  return (
    <main ref={pageRef}>
      <section className="relative overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="AKSUBEB Video Gallery"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />
          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Media Gallery
              </span>
            </div>

            <h1
              className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Video Gallery
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Watch official AKSUBEB videos, school updates, project coverage,
              public briefings and education activities.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">
                Home
              </Link>
              <ChevronRight size={14} />
              <span className="text-[#ff6600]">Video Gallery</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <h2
              className="text-4xl md:text-5xl font-black text-[#06183f]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Latest Videos
            </h2>

            <p className="text-slate-600 mt-3">
              Enjoy official videos directly from YouTube.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {videos.map((video) => (
              <article
                key={video.id}
                className="video-card bg-white rounded-[30px] overflow-hidden border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.12)]"
              >
                <div className="aspect-video bg-black">
                  <iframe
                    src={video.embed_url}
                    title={video.title}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-2 text-[#ff6600] font-bold text-sm mb-3">
                    <PlayCircle size={18} />
                    AKSUBEB Video
                  </div>

                  <h3
                    className="text-2xl font-black text-[#06183f]"
                    style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                  >
                    {video.title}
                  </h3>

                  {video.description && (
                    <p className="text-slate-600 mt-3 leading-relaxed">
                      {video.description}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>

          {!videos.length && (
            <p className="text-center text-slate-500">
              No videos available yet.
            </p>
          )}
        </div>
      </section>
    </main>
  );
};

export default VideoGalleryPage;